'use client'

import {
  ActiveShoppingCartHeaderQuery,
  useActiveShoppingCartHeaderQuery,
} from '@/generated/graphql'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useEffect } from 'react'
import { useStore } from '@/store'

const GUEST_CART_TOKEN_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const INVALID_CART_IDENTIFIER_MESSAGE = 'No valid customer ID provided'
const CART_QUERY_STALE_TIME_MS = 30_000
const CART_QUERY_GC_TIME_MS = 5 * 60_000

const normalizeCartToken = (token: string | null | undefined) => {
  const normalizedToken = token?.trim()
  return normalizedToken && normalizedToken.length > 0 ? normalizedToken : null
}

const isGuestCartToken = (token: string | null | undefined): token is string => {
  const normalizedToken = normalizeCartToken(token)
  return normalizedToken !== null && GUEST_CART_TOKEN_PATTERN.test(normalizedToken)
}

export const useHeaderShoppingCart = () => {
  const user = useStore(state => state.user)
  const authResolved = useStore(state => state.authResolved)
  const shoppingCartId = useStore(state => state.shoppingCartId)
  const hydrated = useStore(state => state.hydrated)
  const setShoppingCartId = useStore(state => state.setShoppingCartId)
  const queryClient = useQueryClient()
  const normalizedShoppingCartId = normalizeCartToken(shoppingCartId)

  const queryVariables = (() => {
    if (authResolved && user?.id) {
      return {
        customerId: user.id,
        includeConventionCart: false,
      }
    }

    if (isGuestCartToken(normalizedShoppingCartId)) {
      return {
        anonymousCartToken: normalizedShoppingCartId,
        includeConventionCart: false,
      }
    }

    return undefined
  })()

  const shouldQueryCart =
    Boolean(
      queryVariables?.anonymousCartToken &&
        queryVariables.anonymousCartToken.trim().length > 0
    ) || Boolean(queryVariables?.customerId)

  const activeShoppingCartHeaderQueryKey = useMemo(
    () =>
      queryVariables
        ? (['ActiveShoppingCart', 'Header', queryVariables] as const)
        : ['ActiveShoppingCartHeaderDisabled'],
    [queryVariables]
  )

  useEffect(() => {
    if (!hydrated || shoppingCartId == null) {
      return
    }

    if (normalizedShoppingCartId !== shoppingCartId) {
      setShoppingCartId(normalizedShoppingCartId)
      return
    }

    if (!isGuestCartToken(normalizedShoppingCartId)) {
      setShoppingCartId(null)
    }
  }, [hydrated, normalizedShoppingCartId, setShoppingCartId, shoppingCartId])

  const activeShoppingCartQuery = useQuery<ActiveShoppingCartHeaderQuery, Error>({
    queryKey: activeShoppingCartHeaderQueryKey,
    queryFn: queryVariables
      ? useActiveShoppingCartHeaderQuery.fetcher(queryVariables)
      : async (): Promise<ActiveShoppingCartHeaderQuery> => ({
          activeShoppingCartHeader: null,
        }),
    enabled: shouldQueryCart && hydrated,
    retry: 1,
    retryDelay: 1000,
    staleTime: CART_QUERY_STALE_TIME_MS,
    gcTime: CART_QUERY_GC_TIME_MS,
  })

  useEffect(() => {
    const cartError = activeShoppingCartQuery.error
    if (
      !cartError ||
      queryVariables?.customerId ||
      !queryVariables?.anonymousCartToken ||
      !cartError.message.includes(INVALID_CART_IDENTIFIER_MESSAGE)
    ) {
      return
    }

    if (queryVariables.anonymousCartToken === normalizedShoppingCartId) {
      setShoppingCartId(null)
    }

    queryClient.removeQueries({
      queryKey: activeShoppingCartHeaderQueryKey,
      exact: true,
    })
  }, [
    activeShoppingCartQuery.error,
    activeShoppingCartHeaderQueryKey,
    normalizedShoppingCartId,
    queryClient,
    queryVariables,
    setShoppingCartId,
  ])

  const cartData = activeShoppingCartQuery.data?.activeShoppingCartHeader

  return {
    cartData,
    totalQuantity: cartData?.totalQuantity ?? 0,
    isPending: activeShoppingCartQuery.isPending,
    isLoading: activeShoppingCartQuery.isLoading,
    isError: activeShoppingCartQuery.isError,
    error: activeShoppingCartQuery.error,
    refetch: activeShoppingCartQuery.refetch,
  }
}
